import api from "./axios";

// Module 11 — Documents. TWENTY-FIVE routes in `functions/src/routers/documentRoutes.js`, mounted at
// `/documents`. The backend is FROZEN: nothing here adds, renames or reshapes an endpoint.
//
// Route gates, all composed after `protect → requirePasswordChanged → requireActive`:
//   uploadGate · selfGate · readGate = requireAuthentication
//        (these are reachable by a CUSTOMER in the route table, so they carry no department or
//         designation gate; the real narrowing is service-side — and see CUSTOMER_UPLOAD_REACHABLE
//         below, because the service in fact refuses customers on both write paths)
//   staffActionGate · verifyGate = requireDepartment(the five OPERATING departments)
//   staffReadGate  = requireDesignation(all four staff designations) — customers denied
//   adminReadGate  = requireDesignation(Master Admin, Department Admin)
//   masterOnlyGate = requireDesignation(Master Admin)
// The Master Admin passes every department gate through the built-in override.
//
// ── SIX RULES THAT SHAPE THIS MODULE ────────────────────────────────────────
// 1. NEVER OVERWRITE. A re-upload is a NEW VERSION on the same document, with its own storage object
//    and its own verification lifecycle beginning at `Uploaded`. A version never inherits the
//    previous version's `Verified` standing, and no object is ever replaced, deleted or re-PUT.
// 2. BYTES NEVER PASS THROUGH THE BACKEND ON THE WAY IN. The upload is a three-step presigned flow:
//    authorize → the browser PUTs straight to storage → confirm. Confirmation reads back what
//    ACTUALLY landed and judges it; only then does a Document exist. See `uploadToStorage`.
// 3. DOWNLOADS ARE STREAMED, AUDITED AND `no-store`. There is NO presigned GET anywhere in the
//    backend — a bearer URL could not be scoped after issue, revoked, or audited on use. Every
//    download emits `document.downloaded`, including the ones that fail.
// 4. THE STORAGE KEY NEVER LEAVES THE BACKEND. Nothing any endpoint returns contains one; a version
//    is addressed by its NUMBER.
// 5. A CLASSIFICATION IS THE (category, type) PAIR, never the type alone. Nine labels are filed
//    under two categories each — Aadhaar is identity in A and address proof in B — and the CATEGORY
//    is what carries the sensitivity. Every call that names a type names its category too.
// 6. NOTHING IS EVER DELETED. No DELETE route exists. A rejected file, a superseded version, a
//    failed upload and an abandoned authorization are all retained — which is the only reason an
//    orphan is reportable at all.

// ── Upload lifecycle (E1–E3) ────────────────────────────────────────────────

/**
 * STEP 1. Reserve a key and issue a presigned PUT.
 *
 * Requires `category`, `type` and at least one linkage slot (`customerId`, or `assetKind`+`assetId`,
 * or `relatedKind`+`relatedId`). `supersedesDocumentId` makes the result a NEW VERSION of that
 * document, and the cited document must carry the SAME classification.
 *
 * Returns `{ authorizationId, uploadUrl, urlExpiresAt, category, type, supersedesDocumentId }`.
 * The storage key is deliberately absent.
 */
export const authorizeUpload = (body) =>
  api.post("/documents/upload-authorizations", body).then((r) => r.data.data);

/**
 * STEP 3. Confirm what actually landed and create the Document or append a version.
 *
 * IDEMPOTENT — a second confirmation returns the same document and version and creates nothing.
 * Four outcomes: `Confirmed` (a document exists), `Failed` (the stored object broke policy; the
 * object is retained and NO document is created), still `Reserved` (nothing has arrived yet), or
 * `Abandoned` (the confirmation window elapsed with nothing at the key).
 */
export const confirmUpload = (authorizationId, body = {}) =>
  api
    .post(`/documents/upload-authorizations/${authorizationId}/confirm`, body)
    .then((r) => r.data.data);

/** The requester's own authorization. No presigned URL is returned — none was ever stored. */
export const fetchUploadAuthorization = (authorizationId) =>
  api.get(`/documents/upload-authorizations/${authorizationId}`).then((r) => r.data.data);

/** Master Admin / Department Admin. Abandoned and failed authorizations, and their retained objects. */
export const fetchAbandonedUploadAuthorizations = (params) =>
  api.get("/documents/upload-authorizations/abandoned", { params }).then((r) => r.data.data);

// ── Reads (E4–E6) ───────────────────────────────────────────────────────────

/**
 * The staff list.
 *
 * ⚠ RETURNS A PLAIN ARRAY, NOT A PAGED ENVELOPE — see UNPAGED_READS below.
 * Filters: `category`, `type`, `standing`, `customerId`, `assetKind`+`assetId`,
 * `relatedKind`+`relatedId`. Sensitive rows the caller may not read are REMOVED, not redacted.
 */
export const fetchDocuments = (params) =>
  api.get("/documents", { params }).then((r) => r.data.data);

/** One document, with every derived value recomputed. */
export const fetchDocument = (id) => api.get(`/documents/${id}`).then((r) => r.data.data);

/** The full version chain — the read that makes the never-overwrite rule verifiable. */
export const fetchDocumentVersions = (id) =>
  api.get(`/documents/${id}/versions`).then((r) => r.data.data);

// ── Corrections, remarks and expiry (E9–E11, E15) ───────────────────────────

/** `{ category, type, reason }`. Refused once the current version is `Verified`. */
export const correctClassification = (id, body) =>
  api.patch(`/documents/${id}/classification`, body).then((r) => r.data.data);

/**
 * `{ customerId?, assetKind?, assetId?, relatedKind?, relatedId?, reason }`.
 * The WHOLE linkage is restated, never patched slot by slot: a patch that cleared the only slot
 * would leave a document linked to nothing, which the linkage rule forbids.
 */
export const correctLinkage = (id, body) =>
  api.patch(`/documents/${id}/linkage`, body).then((r) => r.data.data);

/** `{ text }`. Append-only. Permitted at any standing — a note about a document is not a change to it. */
export const addRemark = (id, body) =>
  api.post(`/documents/${id}/remarks`, body).then((r) => r.data.data);

/**
 * `{ expiryDate, reason }`. Records the DATE only; the `Expired` state is derived at read.
 * Refused (409) unless the type's configuration marks expiry applicable.
 */
export const recordExpiry = (id, body) =>
  api.patch(`/documents/${id}/expiry`, body).then((r) => r.data.data);

// ── Verification lifecycle (E12–E14) ────────────────────────────────────────

/** `{ reason? }` — reason optional. Records `Verified` on the CURRENT version. */
export const verifyDocument = (id, body = {}) =>
  api.post(`/documents/${id}/verify`, body).then((r) => r.data.data);

/** `{ reason }` REQUIRED. Terminal for this version. Nothing is deleted and no object is removed. */
export const rejectDocument = (id, body) =>
  api.post(`/documents/${id}/reject`, body).then((r) => r.data.data);

/**
 * `{ reason }` REQUIRED. The only outcome that expects a successor — and it expects it as a NEW
 * VERSION on this same document, reached by uploading again and citing this document.
 */
export const requireReUpload = (id, body) =>
  api.post(`/documents/${id}/require-re-upload`, body).then((r) => r.data.data);

// ── Entity-scoped reads and expiry (E16–E19) ────────────────────────────────

/**
 * Every document linked to one business entity.
 *
 * THE ONLY WAY A PROPERTY, VENTURE OR PLOT DOCUMENT CAN BE FOUND AT ALL: none of those three frozen
 * models holds a document field, so the document side is the whole of the linkage.
 * ⚠ Returns a plain array.
 */
export const fetchDocumentsByEntity = (kind, id) =>
  api.get(`/documents/by-entity/${kind}/${id}`).then((r) => r.data.data);

// ── R&S PART 4 §4 · HOW OFTEN A REQUIREMENT IS ASKED FOR (owner decision D1) ──
//
// The vocabulary, and the words the configuration screen shows for each value. Absent means
// ONCE_PER_REGISTRATION, which is what every requirement written before Part 4 meant.
export const REQUIREMENT_APPLICATION = Object.freeze({
  ONCE_PER_REGISTRATION: "OncePerRegistration",
  ONCE_PER_PLOT: "OncePerPlot",
  ONCE_PER_CUSTOMER: "OncePerCustomer",
  ONCE_PER_PROPERTY: "OncePerProperty",
});
export const REQUIREMENT_APPLICATION_LABEL = Object.freeze({
  OncePerRegistration: "Once per registration",
  OncePerPlot: "Once per plot",
  OncePerCustomer: "Once per customer",
  OncePerProperty: "Once per property",
});
export const applicationOf = (r) => (r && r.application) || REQUIREMENT_APPLICATION.ONCE_PER_REGISTRATION;

// §4 — only the combinations that mean something for the scope being configured are offered. A
// customer-level requirement cannot sensibly be "once per plot"; a plot-level one cannot be "once per
// customer". Offering them anyway would invite a configuration the checklist could never expand.
export function applicationsFor(entityKind) {
  const A = REQUIREMENT_APPLICATION;
  if (entityKind === "SalesRegistration") {
    return [A.ONCE_PER_REGISTRATION, A.ONCE_PER_PLOT, A.ONCE_PER_CUSTOMER, A.ONCE_PER_PROPERTY];
  }
  if (entityKind === "Customer") return [A.ONCE_PER_CUSTOMER];
  if (entityKind === "Plot") return [A.ONCE_PER_PLOT];
  if (entityKind === "Property" || entityKind === "Venture") return [A.ONCE_PER_PROPERTY];
  return [A.ONCE_PER_REGISTRATION];
}

/** §3/§7 — which version is in force, and whether one is waiting to take effect. */
export const versionLabelOf = (r) => {
  const v = (r && r.currentVersion) || 1;
  const pending = r && r.pendingVersion;
  return pending ? `Version ${v} · v${pending.versionNumber} pending` : `Version ${v}`;
};

/** Present versus the configured expected set. Says so plainly where no set is configured. */
export const fetchEntityCompleteness = (kind, id) =>
  api.get(`/documents/by-entity/${kind}/${id}/completeness`).then((r) => r.data.data);

/**
 * The published expiry read that reminder scheduling is built on. Module 11 owns no lead time and no
 * timer, so "expiring soon" is not a state it can produce. Filter: `before`. ⚠ Plain array.
 */
export const fetchExpiringDocuments = (params) =>
  api.get("/documents/expiring", { params }).then((r) => r.data.data);

/**
 * The customer's own documents. CUSTOMER ONLY — the service refuses a staff caller outright
 * ("This surface is for customers"). Present for completeness; the admin panel never calls it.
 */
export const fetchMyDocuments = (params) =>
  api.get("/documents/my", { params }).then((r) => r.data.data);

// ── Configuration (E20–E22) ─────────────────────────────────────────────────

/** The taxonomy and the effective policy, with every unconfigured value reported as unconfigured. */
export const fetchDocumentConfig = (params) =>
  api.get("/documents/config", { params }).then((r) => r.data.data);

/**
 * MASTER ADMIN. One catalogue row, addressed by its (category, type) PAIR — the path names the type
 * and the body carries the category, because a type is not unique across the catalogue.
 * `{ category, changeReason, active?, verificationRequired?, verifyingDepartment?, expiryApplicable?,
 *    expiryDurationDays?, permittedContentTypes?, maxSizeBytes? }`
 */
/**
 * CS-5B (docs/mds/11 Amendment CS5-M3) — ONE type's reusability. The Master Admin for any type; the relevant
 * Department Admin (R&S for Identity and Address Proof; otherwise the type's verifying department) for this flag only.
 * The server decides; `canGovernReusability` only hides the control from people it would refuse.
 */
export const setTypeReusable = (category, type, body) =>
  api.patch(`/documents/config/catalogue/${encodeURIComponent(category)}/${encodeURIComponent(type)}/reusable`, body)
    .then((r) => r.data.data);

export const updateDocumentTypePolicy = (type, body) =>
  api.patch(`/documents/config/types/${encodeURIComponent(type)}`, body).then((r) => r.data.data);

/** MASTER ADMIN. `{ expected: [{category, type}], changeReason }`. */
export const updateExpectedDocumentSet = (entityKind, body) =>
  api
    .patch(`/documents/config/expected-sets/${encodeURIComponent(entityKind)}`, body)
    .then((r) => r.data.data);

/**
 * The requirement lifecycle (Property Part 1 §13).
 *
 * A requirement is RETIRED, never removed: `Deleted` is an administrative STATE of a row that
 * survives, because documents already filed against the requirement, the verification recorded on
 * them and the audit trail must all keep resolving (§14/§27). There is deliberately no DELETE call
 * here and none on the server.
 */
export const REQUIREMENT_LIFECYCLE = Object.freeze({
  ACTIVE: "Active",
  RETIRED: "Retired",
  DELETED: "Deleted",
  // P5 (MDS P5-D12) — a Department Admin's request; the requirement stays IN FORCE until the Master decides.
  RETIREMENT_REQUESTED: "Retirement Requested",
});

export const REQUIREMENT_LIFECYCLE_VALUES = Object.freeze(Object.values(REQUIREMENT_LIFECYCLE));

/** Active, or awaiting a retirement decision (still applying); Retired and Deleted are history. */
export const isRequirementInForce = (row) =>
  [REQUIREMENT_LIFECYCLE.ACTIVE, REQUIREMENT_LIFECYCLE.RETIREMENT_REQUESTED]
    .includes(row?.lifecycleState || REQUIREMENT_LIFECYCLE.ACTIVE);

// ── Reconciliation (E23–E24) ────────────────────────────────────────────────

/**
 * Of the document references an entity carries, which resolve to a real Document and which do not —
 * and, for those that resolve, whether the Document agrees it is linked to that entity.
 *
 * The caller supplies the entity-side half it already holds. This REPORTS AND STOPS: Module 11 makes
 * no upstream write, so nothing here repairs a divergence.
 *
 * (Resolved — D2. This was unreachable from a browser: the handler read the body of a GET, which
 * every browser discards, so it answered 422 on every call. The list now travels in the query
 * string; the route, gate, validation and response are unchanged.)
 */
export const reportUnresolvedDocumentReferences = ({ references, entityKind, entityId } = {}) =>
  api
    .get("/documents/reconciliation/unresolved-references", {
      params: {
        // Sent as REPEATED query parameters, which axios produces from an array. The previous call
        // put this list in the body of a GET, and browsers discard that body — so the endpoint
        // answered 422 on every call. Nothing about the request's meaning changed; only where the
        // list travels.
        references,
        ...(entityKind ? { entityKind } : {}),
        ...(entityId ? { entityId } : {}),
      },
    })
    .then((r) => r.data.data);

/** What Module 11 believes about its own linkage to an entity, for a caller to check against. */
export const fetchUnmirroredCandidates = (kind, id) =>
  api.get(`/documents/reconciliation/unmirrored/${kind}/${id}`).then((r) => r.data.data);

// ═════════════════════════════════════════════════════════════════════════════
// The presigned PUT — STEP 2, and the only call in the panel that leaves the ERP
// ═════════════════════════════════════════════════════════════════════════════

/**
 * PUT the file straight to object storage under the backend-issued URL.
 *
 * THIS DELIBERATELY DOES NOT USE THE `api` INSTANCE. Three reasons, each load-bearing:
 *   • the URL is absolute and belongs to another origin, so `baseURL` must not apply;
 *   • `withCredentials` must be OFF — sending the ERP session cookie to a storage host would leak
 *     it, and storage rejects unexpected credentials against a signed request anyway;
 *   • the signature covers `Content-Type` (and `Content-Length` where a size limit is configured),
 *     so exactly those headers must be sent and no others. Any extra header breaks the signature.
 *
 * The declared content type must be BYTE-IDENTICAL to the one sent at authorization, or storage
 * refuses the write with 403 SignatureDoesNotMatch.
 *
 * A failure here is usually infrastructure, not the file. A `TypeError` with no status is the
 * signature of a missing bucket CORS rule — the browser never sees the response — and is surfaced
 * as such rather than as "upload failed".
 */
export async function uploadToStorage(uploadUrl, file, declaredContentType) {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": declaredContentType },
      body: file,
    });
  } catch (err) {
    const failure = new Error(
      "The browser could not reach object storage. This is almost always a missing CORS rule on " +
        "the bucket: it must allow PUT from this origin and permit the Content-Type header. The " +
        "presigned URL itself was issued correctly — the authorization exists and can be confirmed " +
        "once the upload succeeds."
    );
    failure.cause = err;
    failure.isCorsOrNetwork = true;
    throw failure;
  }

  if (!response.ok) {
    const failure = new Error(
      response.status === 403
        ? "Object storage refused the write (403). The signature covers the content type and, where " +
          "a size limit is configured, the exact length — a mismatch between what was declared at " +
          "authorization and what was sent will produce this. The URL may also have expired."
        : `Object storage refused the write (${response.status} ${response.statusText}).`
    );
    failure.status = response.status;
    throw failure;
  }

  return true;
}

/**
 * Download a document version through the backend.
 *
 * Streamed, authenticated, audited and `no-store` — there is no presigned GET to fall back on, by
 * design. `responseType: "blob"` keeps the session cookie attached (the `api` instance carries
 * `withCredentials`) and lets a 403 or 404 surface as an ordinary error instead of a broken tab.
 */
export const downloadDocumentBlob = (id, versionNumber) =>
  api
    .get(
      versionNumber === undefined || versionNumber === null
        ? `/documents/${id}/download`
        : `/documents/${id}/versions/${versionNumber}/download`,
      { responseType: "blob" }
    )
    .then((r) => ({ blob: r.data, contentType: r.headers["content-type"] || null }));

/**
 * Hand a downloaded blob to the browser.
 *
 * The backend's own `Content-Disposition` filename is `CODE-vN` with no extension — the extension is
 * never taken from an uploaded filename anywhere in this system, because a name is not evidence of
 * content. The local save name appends the extension the backend itself maps that content type to,
 * for the three types the file policy names, and nothing when the type is unknown.
 */
export function saveBlob(blob, contentType, documentCode, versionNumber) {
  const extension = CONTENT_TYPE_EXTENSIONS[contentType] || "";
  const suffix = extension ? `.${extension}` : "";
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${documentCode}-v${versionNumber}${suffix}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ═════════════════════════════════════════════════════════════════════════════
// Frozen vocabulary (`functions/src/constants/documentEnums.js`)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * TWELVE categories. A–J are the customer-upload categories; K and L exist because the company's own
 * property documents and field-verification evidence would otherwise have no type at all.
 */
export const DOCUMENT_CATEGORY = {
  A_IDENTITY_KYC: "A Identity (KYC)",
  B_ADDRESS_PROOF: "B Address Proof",
  C_FINANCIAL: "C Financial",
  D_LOAN: "D Loan",
  E_REGISTRATION: "E Registration",
  F_NOMINEE_FAMILY: "F Nominee & Family",
  G_PROPERTY_RELATED: "G Property-related",
  H_CUSTOMER_SERVICE: "H Customer-service",
  I_LEGAL: "I Legal",
  J_OTHER: "J Other",
  K_PROPERTY_ASSET: "K Property & Asset Documents",
  L_FIELD_VERIFICATION_EVIDENCE: "L Field Verification Evidence",
};

export const DOCUMENT_CATEGORY_VALUES = Object.values(DOCUMENT_CATEGORY);

/**
 * Types are namespaced BY CATEGORY and are unique within one, never globally. The same label under
 * two categories is not a duplicate to resolve — it is a different classification of the same file,
 * and the category is what carries the sensitivity.
 */
export const DOCUMENT_TYPES_BY_CATEGORY = {
  "A Identity (KYC)": [
    "Aadhaar", "PAN", "Passport", "Voter ID", "Driving Licence", "OCI/PIO", "Photograph",
    "Signature Image",
  ],
  "B Address Proof": [
    "Aadhaar", "Passport", "Driving Licence", "Utility Bill", "Bank Statement", "Rental Agreement",
    "Government Address Proof",
  ],
  "C Financial": [
    "Bank Passbook", "Cancelled Cheque", "Bank Statement", "Salary Slips", "ITR", "Form 16",
    "Income Certificate", "GST Certificate",
  ],
  "D Loan": [
    "Loan Application", "Sanction/Approval Letter", "Bank NOC", "Mortgage Documents", "EMI Schedule",
  ],
  "E Registration": [
    "Signed Sale Agreement", "Registration Application", "Stamp Duty Receipt", "Registration Receipt",
    "Witness Documents", "Affidavits", "Photographs",
  ],
  "F Nominee & Family": [
    "Nominee ID", "Relationship Proof", "Legal Heir Certificate", "Marriage/Birth Certificate",
  ],
  "G Property-related": [
    "Previous Sale Deeds", "Possession/Allotment Letter", "NOC", "Tax Receipts", "EC",
    "Mutation Certificate", "Survey Documents",
  ],
  "H Customer-service": [
    "Name Correction", "Address Change", "Refund Request", "Ownership Transfer", "Complaint",
    "Duplicate Receipt", "Mutation Support",
  ],
  "I Legal": [
    "Court Orders", "Legal Notices", "Affidavits", "PoA", "Settlement/Partition/Gift Deeds",
    "Succession Documents",
  ],
  "J Other": [
    "Authorization Letter", "Company Registration Documents", "Board Resolution", "PoA Authorization",
    "Other Requested Documents",
  ],
  "K Property & Asset Documents": [
    "Sale Deed", "Registration Documents", "Mother Deed", "EC", "NOC", "Tax Receipts",
    "Survey Sketch", "Layout Approval", "Government Approvals", "Aadhaar/PAN of Seller", "Images",
    "Videos", "Other Supporting Documents",
  ],
  "L Field Verification Evidence": [
    "Site Photos", "Boundary Photos", "Plot Photos", "Road Photos", "Videos", "Drone Images",
    "Voice Notes", "GPS Track", "Digital Signature", "Supporting Documents",
  ],
};

/** 87 documented entries across twelve categories, resolving to 78 distinct labels. */
export const DOCUMENTED_DOCUMENT_ENTRY_COUNT = 87;

export const DOCUMENT_TYPE_VALUES = Array.from(
  new Set(Object.values(DOCUMENT_TYPES_BY_CATEGORY).flat())
);

/**
 * SIX labels; FIVE are recorded on a version and `Expired` is DERIVED from the expiry date and never
 * recorded. A standing belongs to a VERSION, never to a document.
 */
export const VERIFICATION_STANDING = {
  UPLOADED: "Uploaded",
  PENDING_VERIFICATION: "Pending Verification",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
  NEEDS_RE_UPLOAD: "Needs Re-upload",
  EXPIRED: "Expired",
};

/** The five a list filter accepts. `Expired` is not among them — it is not stored. */
export const RECORDED_STANDINGS = [
  VERIFICATION_STANDING.UPLOADED,
  VERIFICATION_STANDING.PENDING_VERIFICATION,
  VERIFICATION_STANDING.VERIFIED,
  VERIFICATION_STANDING.REJECTED,
  VERIFICATION_STANDING.NEEDS_RE_UPLOAD,
];

export const TERMINAL_STANDINGS = [
  VERIFICATION_STANDING.VERIFIED,
  VERIFICATION_STANDING.REJECTED,
];

/**
 * The frozen transition map. Three shapes are deliberate:
 *   `Uploaded` has exactly ONE exit. Where a type does not require verification the version simply
 *     REMAINS at `Uploaded` — that is an absence of transition, not a "not required" state.
 *   `Verified`, `Rejected` and `Needs Re-upload` have NO exits. Nothing re-opens a version.
 *   `Expired` is not a key at all: nothing transitions into or out of it.
 */
export const STANDING_TRANSITIONS = {
  Uploaded: ["Pending Verification"],
  "Pending Verification": ["Verified", "Rejected", "Needs Re-upload"],
  Verified: [],
  Rejected: [],
  "Needs Re-upload": [],
};

export const STANDINGS_REQUIRING_REASON = [
  VERIFICATION_STANDING.REJECTED,
  VERIFICATION_STANDING.NEEDS_RE_UPLOAD,
];

/**
 * `NotConfigured` and `NotApplicable` are DIFFERENT ANSWERS and both are honest: the first means no
 * ruling exists for the type, the second that the company has ruled it does not expire. Neither is
 * ever reported as valid and neither as expired.
 */
export const EXPIRY_STATE = {
  NOT_CONFIGURED: "NotConfigured",
  NOT_APPLICABLE: "NotApplicable",
  VALID: "Valid",
  EXPIRED: "Expired",
};

export const EXPIRY_STATE_LABEL = {
  NotConfigured: "Expiry not configured",
  NotApplicable: "Does not expire",
  Valid: "Valid",
  Expired: "Expired",
};

/** The four MECHANICAL states of an upload authorization. These never appear on a Document. */
export const UPLOAD_STATE = {
  RESERVED: "Reserved",
  CONFIRMED: "Confirmed",
  FAILED: "Failed",
  ABANDONED: "Abandoned",
};

export const SENSITIVITY = { SENSITIVE: "Sensitive", STANDARD: "Standard" };

/** Seeded from the security rule's own words: identity and financial documents. */
export const SEEDED_SENSITIVE_CATEGORIES = [
  DOCUMENT_CATEGORY.A_IDENTITY_KYC,
  DOCUMENT_CATEGORY.C_FINANCIAL,
];

export const VISIBILITY_CLASS = {
  ASSET_SCOPED: "AssetScoped",
  CUSTOMER_PERSONAL: "CustomerPersonal",
};

/** None of these three frozen models holds a document field — the document side is the whole linkage. */
export const ASSET_KIND = { PROPERTY: "Property", VENTURE: "Venture", PLOT: "Plot" };
export const ASSET_KIND_VALUES = Object.values(ASSET_KIND);

/** SEVEN related-record kinds — every frozen model that declares a document reference. */
export const RELATED_RECORD_KIND = {
  BOOKING: "Booking",
  PAYMENT_RECORD: "PaymentRecord",
  SERVICE_REQUEST: "ServiceRequest",
  LEGAL_CASE: "LegalCase",
  VERIFICATION: "Verification",
  REALLOCATION: "Reallocation",
  OWNERSHIP_RECORD: "OwnershipRecord",
};
export const RELATED_RECORD_KIND_VALUES = Object.values(RELATED_RECORD_KIND);

export const CUSTOMER_KIND = "Customer";

/**
 * R&S PART 4 (browser defect P4-BROWSER-01) — THE WORKFLOW KINDS.
 *
 * A requirement set can also belong to a WORKFLOW rather than to a record: R&S Part 3 added
 * `SalesRegistration`, the registration checklist the Master Admin configures (owner decision D4).
 *
 * It was added to the model and — after P4-INSPECT-03 — to the service, and it was still impossible to
 * configure, because this list is what the Entity kind dropdown offers and it had never heard of it.
 * Three separate walls stood around one feature: the service refused the kind, the model refused the
 * checklist it produced, and the screen would not let anybody ask for it in the first place.
 */
export const WORKFLOW_KIND = { SALES_REGISTRATION: "SalesRegistration" };
export const WORKFLOW_KIND_VALUES = Object.values(WORKFLOW_KIND);

/** Every entity kind the by-entity, completeness and reconciliation reads accept. CASE-SENSITIVE. */
export const ENTITY_KIND_VALUES = [
  CUSTOMER_KIND,
  ...ASSET_KIND_VALUES,
  ...RELATED_RECORD_KIND_VALUES,
];

/**
 * The kinds an EXPECTED SET may be configured for. A superset of the reads above: a workflow has a
 * requirement set but is not an entity you file a document against, so the two lists are genuinely
 * different and are kept apart rather than one being quietly widened.
 */
export const EXPECTED_SET_ENTITY_KIND_VALUES = [
  ...ENTITY_KIND_VALUES,
  ...WORKFLOW_KIND_VALUES,
];

/** The two things a correction may change — everything else is corrected by a new version. */
export const CORRECTION_TARGET = { CLASSIFICATION: "Classification", LINKAGE: "Linkage" };

/**
 * The three content types the security rule names, with the canonical extension the backend derives
 * from the CONTENT TYPE — never from a supplied filename. The set beyond these three is configuration.
 */
export const CONTENT_TYPE_EXTENSIONS = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
};

export const SEEDED_PERMITTED_CONTENT_TYPES = Object.keys(CONTENT_TYPE_EXTENSIONS);

export const DEPARTMENTS = {
  ADMINISTRATION: "Administration",
  PROPERTIES: "Properties",
  FIELD_OPERATIONS: "Field Operations",
  LEGAL: "Legal",
  REGISTRATION_SALES: "Registration cum Sales",
  CUSTOMER_SUPPORT: "Customer Support",
};
export const DEPARTMENT_VALUES = Object.values(DEPARTMENTS);

/** Customer Support may NEVER be a verifying department — the configuration endpoint refuses it. */
export const VERIFYING_DEPARTMENT_CHOICES = DEPARTMENT_VALUES.filter(
  (d) => d !== DEPARTMENTS.CUSTOMER_SUPPORT
);

/** The five operating departments every staff-action and verification route admits. */
export const OPERATING_DEPARTMENTS = [
  DEPARTMENTS.CUSTOMER_SUPPORT,
  DEPARTMENTS.REGISTRATION_SALES,
  DEPARTMENTS.LEGAL,
  DEPARTMENTS.PROPERTIES,
  DEPARTMENTS.FIELD_OPERATIONS,
];

// ═════════════════════════════════════════════════════════════════════════════
// Recorded backend limitations — surfaced, never worked around
// ═════════════════════════════════════════════════════════════════════════════

/**
 * ⚠ FOUR READS RETURN A PLAIN ARRAY WITH NO PAGINATION AT ALL.
 *
 * `listDocuments`, `listDocumentsByEntity`, `listExpiringDocuments` and `getMyDocuments` each run a
 * `find()` with a sort and no `skip`/`limit`, and return the mapped array directly — no `page`, no
 * `total`, no `totalPages`. That is a deliberate difference from Modules 7–10, whose lists are all
 * server-paged.
 *
 * The panel therefore renders no pager on these surfaces. A pager wired to parameters the server
 * ignores would silently show page 1 for every page and read as "there is no more data".
 */
export const UNPAGED_READS = [
  "GET /documents",
  "GET /documents/by-entity/:kind/:id",
  "GET /documents/expiring",
  "GET /documents/my",
];

/**
 * No published endpoint of this module is unreachable from a browser (D2).
 *
 * `GET /documents/reconciliation/unresolved-references` previously was: it is a GET whose handler
 * read `req.body`, and XMLHttpRequest discards the body of a GET by specification, so `references`
 * arrived undefined and the service answered 422 every time from any browser client. The handler now
 * reads the list from the query string (repeated `references` parameters, or one comma-separated
 * value), with the body still honoured for non-browser callers. Route, gate, validation and response
 * envelope are unchanged.
 *
 * Kept as an empty list rather than deleted, so the panel keeps a single place to declare such a gap
 * if one is ever introduced again.
 */
export const UNREACHABLE_ENDPOINTS = [];

/**
 * ✅ RESOLVED (D5). Both filters now work over HTTP.
 *
 * This previously recorded that `getDocumentConfig` tested `filters.activeOnly === true` and
 * `listAbandonedUploadAuthorizations` tested `filters.includeReserved !== false` — STRICT boolean
 * comparisons that a query string, which Express delivers as STRINGS, could never satisfy.
 *
 * Both now accept the string form the wire actually carries (`x === true || x === "true"`, and the
 * `"false"` form for `includeReserved`), so `?activeOnly=true` narrows the catalogue and
 * `?includeReserved=false` genuinely excludes Reserved authorizations. The list is empty because
 * nothing in this module is unreachable any more, not because nothing was ever checked.
 */
export const UNREACHABLE_BOOLEAN_FILTERS = [];

/**
 * ⚠ REMARK AND CORRECTION CONTENT IS WRITE-ONLY OVER HTTP.
 *
 * Remarks and corrections are stored append-only, and `addRemark`, `correctClassification` and
 * `correctLinkage` all write them — but the read projection returns `remarkCount` and
 * `correctionCount` and nothing more. No endpoint returns the text of a remark, the reason for a
 * correction, or its previous → new values.
 *
 * The panel therefore shows the COUNTS and says where the content lives: every one of those acts
 * emits an audit event carrying its reason and its previous and new values, so the record exists —
 * it is simply not readable through this module's own API.
 */
export const REMARK_AND_CORRECTION_CONTENT_READABLE = false;

/**
 * THE CUSTOMER UPLOAD SURFACE IS REACHABLE (resolved — D1).
 *
 * It previously was not: the route table described E1 and E2 as customer-writable, but
 * `authorizeUpload` and `confirmUpload` both called `assertStaffWriter`, which refused a customer
 * outright, so the two endpoints MDS §12 declares customer-writable could not be used.
 *
 * The staff-only gate has been removed from those two operations and replaced by the linkage bound
 * MDS §11.3 actually specifies: a customer may authorize an upload against their OWN customer record
 * or an asset they hold (resolved through Module 7), may supersede only a document they hold, and may
 * confirm only the authorization they themselves requested. Everything else — verify, reject,
 * re-classify, re-link, and any staff-only linkage — remains refused.
 */
export const CUSTOMER_UPLOAD_REACHABLE = true;

/**
 * ✅ RESOLVED (D4). `Uploaded → Pending Verification` is now written on confirmation.
 *
 * This previously recorded that no code path ever wrote `Pending Verification`, so a confirmed
 * version stayed at `Uploaded` and any "awaiting verification" count had to query `Uploaded`.
 *
 * `confirmUpload` now sets the standing from the resolved type policy — `Pending Verification` where
 * the type requires verification, the first recorded standing where it does not. Under PD8 an
 * unconfigured requirement is TREATED AS REQUIRED, and no type is configured, so every one of the 87
 * catalogue rows resolves to `verificationRequired: true`: a newly confirmed version reaches
 * `Pending Verification`.
 *
 * ⚠ BOTH STANDINGS ARE LIVE. Documents confirmed BEFORE this fix remain at `Uploaded` and are never
 * back-filled — nothing rewrites history. An "awaiting review" view must therefore count `Uploaded`
 * AND `Pending Verification`, which is why `StandingBadge` still labels `Uploaded` as awaiting review
 * for a verification-required type.
 */
export const PENDING_VERIFICATION_IS_UNREACHABLE = false;

/**
 * ⚠ SIX COMPANY RULINGS HAVE NO CONFIGURATION ENDPOINT.
 *
 * Category sensitivity, the catalogue-wide permitted content types and maximum size, the customer
 * upload quota and rate limit, and the presigned-URL lifetime and confirmation window are all
 * settable only by `updateGlobalDocumentPolicy`, which the endpoint table does not publish. The
 * settings page shows their current values read-only and says where they can be changed.
 */
export const GLOBAL_POLICY_ENDPOINT_EXISTS = false;

// ═════════════════════════════════════════════════════════════════════════════
// Predicates mirroring the service guards
// ═════════════════════════════════════════════════════════════════════════════
// Every one of these is COSMETIC. The service re-checks all of it on every request. They exist so
// the panel does not offer a door that will not open, and can explain why a door is closed.

export const nextStandings = (standing) => STANDING_TRANSITIONS[standing] || [];

export const isTerminalStanding = (standing) => TERMINAL_STANDINGS.includes(standing);

/**
 * Whether a verification decision would be accepted for this document's current standing.
 *
 * Mirrors `assertDecisionPermitted`, including its compensation: a decision is accepted from
 * `Pending Verification`, OR from `Uploaded` when the type requires verification — which, since
 * nothing ever writes `Pending Verification`, is the path every real decision actually takes.
 */
export function decisionPermitted(currentStanding, target, verificationRequired) {
  if (!currentStanding) return false;
  if (nextStandings(currentStanding).includes(target)) return true;
  return (
    currentStanding === VERIFICATION_STANDING.UPLOADED &&
    verificationRequired === true &&
    nextStandings(VERIFICATION_STANDING.PENDING_VERIFICATION).includes(target)
  );
}

/** A classification or a linkage may be corrected only while the current version is not `Verified`. */
export const isCorrectable = (doc) =>
  Boolean(doc) && doc.currentStanding !== VERIFICATION_STANDING.VERIFIED;

/**
 * Why the correction panels are closed, in the server's own terms — or null when they are open.
 */
export function correctionBlocker(doc) {
  if (!doc) return "Loading…";
  if (doc.currentStanding === VERIFICATION_STANDING.VERIFIED) {
    return (
      "A verified document may only be changed by a new version. Once a reviewer has approved a file " +
      "under a classification, that pairing is part of what was approved."
    );
  }
  return null;
}

/** An expiry date may be recorded only where the type's configuration marks expiry applicable. */
export function expiryBlocker(doc) {
  if (!doc) return "Loading…";
  if (doc.expiryState === EXPIRY_STATE.NOT_APPLICABLE) {
    return "This document type is configured not to carry an expiry.";
  }
  if (doc.expiryState === EXPIRY_STATE.NOT_CONFIGURED && !doc.expiryDate) {
    return (
      "Expiry applicability is not configured for this document type, so no date can be recorded. " +
      "A Master Admin configures it per type on the document settings page."
    );
  }
  return null;
}

/**
 * The types of one category, or an empty list. Used everywhere a type is chosen, because a type is
 * only meaningful inside its category.
 */
export const typesForCategory = (category) => DOCUMENT_TYPES_BY_CATEGORY[category] || [];

/** Whether a linkage payload satisfies the at-least-one-slot rule, and pairs kind with id. */
export function linkageIsValid({ customerId, assetKind, assetId, relatedKind, relatedId }) {
  if (Boolean(assetKind) !== Boolean(assetId)) return false;
  if (Boolean(relatedKind) !== Boolean(relatedId)) return false;
  return Boolean(customerId || assetKind || relatedKind);
}

/** A one-line rendering of a document's linkage, in the same shape the correction log records. */
export function describeLinkage(linkage) {
  if (!linkage) return "none";
  return (
    [
      linkage.customerId ? `Customer:${linkage.customerId}` : null,
      linkage.assetKind ? `${linkage.assetKind}:${linkage.assetId}` : null,
      linkage.relatedKind ? `${linkage.relatedKind}:${linkage.relatedId}` : null,
    ]
      .filter(Boolean)
      .join(" | ") || "none"
  );
}
